import axios from 'axios';
import type {
  ScriptInfo,
  SQLExecutionResult,
  FileNode,
  CSVPreview,
  HealthStatus,
} from '../types';

const TOKEN_KEY = 'auth_token';

// API base URL
// - In development: '/api' (proxied by Vite to localhost:8080)
// - In production: Use VITE_API_URL env var for direct backend URL
//   For Azure: Set VITE_API_URL=https://api-25fit-analytics.azurewebsites.net
//   For Vercel: '/api' (proxied by rewrites)
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 300000, // 5 minutes for long-running queries
});

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses - redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const scriptsApi = {
  getSQLScripts: async (): Promise<ScriptInfo[]> => {
    const response = await api.get<ScriptInfo[]>('/scripts/sql');
    return response.data;
  },
};

export const executeApi = {
  executeSQL: async (
    sqlFile: string,
    fromDate?: string,
    toDate?: string
  ): Promise<SQLExecutionResult> => {
    const response = await api.post<SQLExecutionResult>('/execute/sql', {
      sqlFile,
      fromDate,
      toDate,
    });
    return response.data;
  },
};

export const filesApi = {
  getFileTree: async (): Promise<FileNode[]> => {
    const response = await api.get<FileNode[]>('/files/tree');
    return response.data;
  },

  previewCSV: async (filePath: string, limit = 100): Promise<CSVPreview> => {
    const response = await api.get<CSVPreview>(`/files/preview/${filePath}`, {
      params: { limit },
    });
    return response.data;
  },

  downloadCSV: (filePath: string): string => {
    return `/api/files/download/${filePath}`;
  },
};

export const healthApi = {
  getHealth: async (): Promise<HealthStatus> => {
    const response = await api.get<HealthStatus>('/health');
    return response.data;
  },
};

export default api;
